/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FileType } from 'src/core/types/FileType';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { LuisId } from 'src/luis/types/Resource';
import { AppVersionStore } from 'src/luis/types/stores/apps/AppVersionStore';

export type AppVersionTableCommandBarProps = TableCommandBarProps<AppVersionStore> & {
    /**
     * Total number of current app versions.
     */
    appVersionsCount: number;

    /**
     * The current active version id
     */
    activeVersionId: LuisId;

    /**
     * Is current version trained or not.
     */
    isVersionTrained: boolean;

    /**
     * Callback for importing an app version.
     */
    onImportAppVersion: (fileType: FileType) => boolean | void;

    /**
     * Callback for renaming an app version.
     */
    onRenameAppVersion: () => boolean | void;

    /**
     * Callback for importing an app version.
     */
    onCloneAppVersion: () => boolean | void;

    /**
     * Callback for renaming an app version.
     */
    onActivateAppVersion: () => boolean | void;

    /**
     * Callback for exporting an app version
     */
    onExportAppVersion: (fileType: FileType) => boolean | void;

    /**
     * Callback for deleting app versions.
     */
    onDeleteAppVersions: () => boolean | void;

    /**
     * User role to determine the actions.
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'apps.appVersionTableCommandBar.searchPlaceholder',
        defaultMessage: 'Search versions ...'
    },
    importAppVersionButton: {
        id: 'apps.appVersionTableCommandBar.importAppVersionButton',
        defaultMessage: 'Import'
    },
    importAppVersionJsonButton: {
        id: 'apps.appVersionTableCommandBar.importAppVersionJsonButton',
        defaultMessage: 'Import as JSON'
    },
    importAppVersionLuButton: {
        id: 'apps.appVersionTableCommandBar.importAppVersionLuButton',
        defaultMessage: 'Import as LU'
    },
    renameAppVersionButton: {
        id: 'apps.appVersionTableCommandBar.renameAppVersionButton',
        defaultMessage: 'Rename'
    },
    cloneAppVersionButton: {
        id: 'apps.appVersionTableCommandBar.cloneAppVersionButton',
        defaultMessage: 'Clone'
    },
    activateAppVersionButton: {
        id: 'apps.appVersionTableCommandBar.activateAppVersionButton',
        defaultMessage: 'Activate'
    },
    exportAppVersionButton: {
        id: 'apps.appVersionTableCommandBar.exportAppVersionButton',
        defaultMessage: 'Export'
    },
    exportAppVersionJsonButton: {
        id: 'apps.appVersionTableCommandBar.exportAppVersionJsonButton',
        defaultMessage: 'Export as JSON'
    },
    exportAppVersionLuButton: {
        id: 'apps.appVersionTableCommandBar.exportAppVersionLuButton',
        defaultMessage: 'Export as LU'
    },
    exportAppVersionContainerButton: {
        id: 'apps.appVersionTableCommandBar.exportAppVersionContainerButton',
        defaultMessage: 'Export for containers'
    },
    deleteAppVersionButton: {
        id: 'apps.appVersionTableCommandBar.deleteAppVersionButton',
        defaultMessage: 'Delete'
    }
});

/**
 * Represents a command bar for the application versions table.
 *
 * @param props The properties for the application versions table command bar.
 */
export const AppVersionTableCommandBar = (props: AppVersionTableCommandBarProps) => {
    const {
        selectedItems = [],
        activeVersionId,
        isVersionTrained,
        query,
        onQueryChange,
        appVersionsCount,
        userRole,
        displayedUserRole
    } = props;
    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'import',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="importButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            split
                            role="menuitem"
                            iconProps={{ iconName: 'Upload' }}
                            disabled={!canWrite || item.disabled}
                            menuProps={{
                                items: [
                                    {
                                        key: 'importJson',
                                        name: localize(messages.importAppVersionJsonButton),
                                        onClick: () => props.onImportAppVersion('json')
                                    },
                                    {
                                        key: 'importLu',
                                        name: localize(messages.importAppVersionLuButton),
                                        onClick: () => props.onImportAppVersion('lu')
                                    }
                                ]
                            }}
                            data-automation-id="importButton"
                            name={localize(messages.importAppVersionButton)}
                            text={localize(messages.importAppVersionButton)}
                            ariaLabel={localize(messages.importAppVersionButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'rename',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="renameButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            data-automation-id="renameButton"
                            onClick={props.onRenameAppVersion}
                            iconProps={{ iconName: 'Rename' }}
                            disabled={!canWrite || item.disabled}
                            text={localize(messages.renameAppVersionButton)}
                            name={localize(messages.renameAppVersionButton)}
                            ariaLabel={localize(messages.renameAppVersionButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'clone',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    role="menuitem"
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="cloneButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            iconProps={{ iconName: 'Copy' }}
                            data-automation-id="cloneButton"
                            onClick={props.onCloneAppVersion}
                            disabled={!canWrite || item.disabled}
                            text={localize(messages.cloneAppVersionButton)}
                            name={localize(messages.cloneAppVersionButton)}
                            ariaLabel={localize(messages.cloneAppVersionButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },

        {
            key: 'activate',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="activateButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            data-automation-id="activateButton"
                            onClick={props.onActivateAppVersion}
                            disabled={!canWrite || item.disabled}
                            iconProps={{ iconName: 'Completed' }}
                            text={localize(messages.activateAppVersionButton)}
                            name={localize(messages.activateAppVersionButton)}
                            ariaLabel={localize(messages.activateAppVersionButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'export',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="exportButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            split
                            role="menuitem"
                            data-automation-id="exportButton"
                            iconProps={{ iconName: 'Download' }}
                            disabled={!canWrite || item.disabled}
                            name={localize(messages.exportAppVersionButton)}
                            text={localize(messages.exportAppVersionButton)}
                            ariaLabel={localize(messages.exportAppVersionButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            menuProps={{
                                items: [
                                    {
                                        key: 'exportJson',
                                        name: localize(messages.exportAppVersionJsonButton),
                                        onClick: () => props.onExportAppVersion('json')
                                    },
                                    {
                                        key: 'exportLu',
                                        name: localize(messages.exportAppVersionLuButton),
                                        onClick: () => props.onExportAppVersion('lu')
                                    },
                                    {
                                        key: 'exportContainer',
                                        name: localize(messages.exportAppVersionContainerButton),
                                        onClick: () => props.onExportAppVersion('gz'),
                                        disabled: !isVersionTrained
                                    }
                                ]
                            }}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canDeleteData || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            data-automation-id="deleteButton"
                            iconProps={{ iconName: 'Delete' }}
                            onClick={props.onDeleteAppVersions}
                            disabled={!canDeleteData || item.disabled}
                            text={localize(messages.deleteAppVersionButton)}
                            name={localize(messages.deleteAppVersionButton)}
                            ariaLabel={localize(messages.deleteAppVersionButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    const items: ICommandBarItemProps[] = getCommandBarItems(commands, selectedItems, activeVersionId, appVersionsCount);

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedSearchBox
                    size={30}
                    value={query}
                    ariaLabel={localize(messages.searchPlaceholder)}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                />
            )
        }
    ];

    return <LuisCommandBar items={items} farItems={farItems} />;
};

/**
 * Gets the commands to display in the command bar based on the
 * selected items and the current version states.
 *
 * @param commands All the commands supported.
 * @param selectedVersions The versions that are currently selected.
 * @param activeVersionId The current active version id.
 * @returns An array of filtered commands.
 */
const getCommandBarItems = (
    commands: ICommandBarItemProps[],
    selectedVersions: AppVersionStore[],
    activeVersionId: LuisId,
    totalItemsNumber: number
): ICommandBarItemProps[] => {
    const updatedCommands: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['import'],
        ['rename', 'clone', 'activate', 'export', 'delete'],
        ['delete'],
        selectedVersions.length
    );

    /**
     *  Ensure that if only one version exists, and is selected, can't be deleted.
     */
    if (selectedVersions.length === 1 && totalItemsNumber === 1) {
        updatedCommands.find(c => c.key === 'delete').disabled = true;
    }

    /**
     *  Ensure that if all versions are selected, can't be deleted.
     */
    if (selectedVersions.length > 1 && selectedVersions.length === totalItemsNumber) {
        updatedCommands.find(c => c.key === 'delete').disabled = true;
    }

    /**
     * Ensure that if one version is selected, and it is the active version, then don't
     * display the activate command, as the version is already active.
     */
    if (selectedVersions.length === 1 && activeVersionId && selectedVersions[0].id === activeVersionId) {
        updatedCommands.find(c => c.key === 'activate').disabled = true;
    }

    return updatedCommands;
};
